import {CardDetail, CardSummary} from '@/shared/models/card';
import {WithJsonataInput} from '@/shared/utils/jsonata';

export enum CardTextMainColor {
  BLACK = 'BLACK',
  WHITE = 'WHITE'
}

export enum DesignTemplateCode {
  /** 공동체카드, 환자카드 */
  HEADLINE_A = 'HEADLINE_A',
  /** 내차인증카드 */
  HEADLINE_B = 'HEADLINE_B',
  /** 카카오사원증(디자인만 사용하고 도어키는 별도 템플릿) */
  HEADLINE_C = 'HEADLINE_C',
  /** 자격카드, 동네카드 */
  PROFILE_A = 'PROFILE_A',
  /** 틴카드 */
  PROFILE_B = 'PROFILE_B',
  /** 톡사원증 */
  PROFILE_BADGE = 'PROFILE_BADGE',
  /** 톡학생증 */
  PROFILE_WEBP = 'PROFILE_WEBP',
  /** 코리아둘레길카드 */
  IMAGE = 'IMAGE',
  /** 카카오사원증 */
  HEADLINE_DOORKEY = 'HEADLINE_DOORKEY'
}

/** [GET] /cards/groups/{groupCode} 응답 */
export interface CardGroupResponse {
  group: CardGroup;
  cards: Card[];
}

/** 카드 그룹 정보 ex. 톡학생증, 톡사원증 등등.. */
interface CardGroup {
  name: string;
  code: string;
  actions: CardGroupAction[];
}

/** 화면 우측 상단 기능 버튼 */
interface CardGroupAction {
  name: string;
  landingUrl: string;
  sequence: number;
}

/** 카드 그룹 내 각 카드에 대한 정보 */
export interface Card {
  designTemplateCode: DesignTemplateCode;
  cardId: string;
  validStartAt: string;
  validEndAt: string;
  content: CardContent;
  buttons: CardButton[];
  overview: CardOverview;
}

/** 카드에 표시되는 내용 */
export interface CardContent {
  textColor?: CardTextMainColor;
  backgroundImageUrl: string;
  title?: WithJsonataInput;
  subTitle?: WithJsonataInput;
  info1?: CardSection;
  info2?: CardSection;
  doorKey?: DoorKey;
  badge?: WithJsonataInput;
}

/** Profile 템플릿에서 사용되는 정보 */
export interface CardSection {
  title: string;
  value: string;
  jsonataInput?: object | null;
}

/** 인하우스 사원증 도어키 정보 */
interface DoorKey {
  type: string;
  credential_data: {
    cardNumber: string;
    cardNumberBit: string;
    encType: string;
    endDate: string;
  };
}

/** 카드 아래 기능 버튼 */
export interface CardButton {
  iconUrl: string;
  value: string;
  landingUrl: string;
  sequence: number;
}

/** 카드 전체 목록 바텀시트 하단에 노출되는 내용 */
interface CardOverview {
  title: string;
  section1: string;
  section2: string;
}

export interface BaseCardTemplateProps {
  onClickDetail?: () => void;
  isThumbnail?: boolean;
  isActive?: boolean;
}

export const enum TemplateKeyType {
  TITLE = 'title',
  SUBTITLE = 'subTitle',
  INFO1 = 'info1',
  INFO2 = 'info2',
  BACKGROUND_IMAGE_URL = 'backgroundImageUrl',
  TEXT_COLOR = 'textColor',
  BADGE = 'badge',
  DOORKEY = 'doorKey'
}

/** 템플릿 타입별로 필요한 키 정의 */
export const TEMPLATE_KEYS = {
  HEADLINE: [
    TemplateKeyType.TITLE,
    TemplateKeyType.SUBTITLE,
    TemplateKeyType.BACKGROUND_IMAGE_URL,
    TemplateKeyType.TEXT_COLOR
  ],
  PROFILE: [
    TemplateKeyType.TITLE,
    TemplateKeyType.INFO1,
    TemplateKeyType.INFO2,
    TemplateKeyType.BACKGROUND_IMAGE_URL,
    TemplateKeyType.TEXT_COLOR
  ],
  PROFILE_WITH_SUBTITLE: [
    TemplateKeyType.TITLE,
    TemplateKeyType.SUBTITLE,
    TemplateKeyType.INFO1,
    TemplateKeyType.INFO2,
    TemplateKeyType.BACKGROUND_IMAGE_URL,
    TemplateKeyType.TEXT_COLOR
  ],
  PROFILE_BADGE: [
    TemplateKeyType.TITLE,
    TemplateKeyType.BADGE,
    TemplateKeyType.INFO1,
    TemplateKeyType.INFO2,
    TemplateKeyType.BACKGROUND_IMAGE_URL,
    TemplateKeyType.TEXT_COLOR
  ],
  IMAGE: [TemplateKeyType.BACKGROUND_IMAGE_URL],
  HEADLINE_DOORKEY: [
    TemplateKeyType.TITLE,
    TemplateKeyType.SUBTITLE,
    TemplateKeyType.BACKGROUND_IMAGE_URL,
    TemplateKeyType.TEXT_COLOR,
    TemplateKeyType.DOORKEY
  ]
} as const;

type HeadlineTemplateKey = (typeof TEMPLATE_KEYS.HEADLINE)[number];
type ProfileTemplateKey = (typeof TEMPLATE_KEYS.PROFILE)[number];
type ProfileWithSubTitleTemplateKey = (typeof TEMPLATE_KEYS.PROFILE_WITH_SUBTITLE)[number];
type ImageTemplateKey = (typeof TEMPLATE_KEYS.IMAGE)[number];
type ProfileBadgeTemplateKey = (typeof TEMPLATE_KEYS.PROFILE_BADGE)[number];
type HeadlineDoorkeyTemplateKey = (typeof TEMPLATE_KEYS.HEADLINE_DOORKEY)[number];

type HeadlineTemplateContent = Required<Pick<CardContent, HeadlineTemplateKey>>;
type ProfileTemplateContentWithSubTitle = Required<Pick<CardContent, ProfileWithSubTitleTemplateKey>>;
type ProfileTemplateContentWithoutSubTitle = Required<Pick<CardContent, ProfileTemplateKey>>;
type ImageTemplateContent = Required<Pick<CardContent, ImageTemplateKey>>;
type ProfileBadgeTemplateContent = Required<Pick<CardContent, ProfileBadgeTemplateKey>>;
type HeadlineDoorkeyTemplateContent = Required<Pick<CardContent, HeadlineDoorkeyTemplateKey>>;

export type HeadlineTemplateProps = HeadlineTemplateContent & BaseCardTemplateProps;
export type ProfileTemplatePropsWithSubTitle = ProfileTemplateContentWithSubTitle & BaseCardTemplateProps;
export type ProfileTemplatePropsWithoutSubTitle = ProfileTemplateContentWithoutSubTitle & BaseCardTemplateProps;
export type ProfileBadgeTemplateProps = ProfileBadgeTemplateContent & BaseCardTemplateProps;
export type ImageTemplateProps = ImageTemplateContent & BaseCardTemplateProps;
export type HeadlineDoorkeyTemplateProps = HeadlineDoorkeyTemplateContent & BaseCardTemplateProps;

export interface CardDetailsResponse {
  backgroundImageUrl?: string;
  /** 카드 상세에 노출되는 정보 */
  info: CardDetail[];
  /** 상세에서 공통으로 노출되는 카드 정보 */
  summaryInfo: CardSummary;
  /** 상세 하단에 노출되는 추가 정보 */
  descriptions: string[];
}
